"use client";

/** Notify all open AVT tabs that donation records changed. */
export function notifyDonationsChanged() {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new Event("avt-donations-changed"));
  } catch {
    // ignore
  }
  try {
    const channel = new BroadcastChannel("avt-donations");
    channel.postMessage({ type: "changed", at: Date.now() });
    channel.close();
  } catch {
    // ignore (unsupported browsers)
  }
}

type Options = {
  onChange: () => void;
  /** Fallback poll while SSE is unavailable (ms). */
  pollMs?: number;
};

/** Live-refresh donations list via SSE + BroadcastChannel + quiet polling. */
export function subscribeDonationsLive({ onChange, pollMs = 8000 }: Options) {
  if (typeof window === "undefined") return () => {};

  let closed = false;
  let source: EventSource | null = null;
  let channel: BroadcastChannel | null = null;
  let pollTimer: number | null = null;
  let reloadTimer: number | null = null;

  const reload = () => {
    if (closed) return;
    if (reloadTimer != null) window.clearTimeout(reloadTimer);
    reloadTimer = window.setTimeout(() => {
      if (!closed) onChange();
    }, 120);
  };

  const onLocal = () => reload();
  window.addEventListener("avt-donations-changed", onLocal);
  window.addEventListener("focus", onLocal);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") reload();
  });

  try {
    channel = new BroadcastChannel("avt-donations");
    channel.onmessage = () => reload();
  } catch {
    channel = null;
  }

  try {
    source = new EventSource("http://localhost:5002/api/admin/donations/live", {
      withCredentials: true,
    });
    source.addEventListener("donations", () => reload());
    source.onerror = () => {
      // Keep polling fallback when SSE drops.
    };
  } catch {
    source = null;
  }

  pollTimer = window.setInterval(() => {
    if (document.visibilityState === "visible") reload();
  }, pollMs);

  return () => {
    closed = true;
    window.removeEventListener("avt-donations-changed", onLocal);
    window.removeEventListener("focus", onLocal);
    if (reloadTimer != null) window.clearTimeout(reloadTimer);
    if (pollTimer != null) window.clearInterval(pollTimer);
    try {
      channel?.close();
    } catch {
      // ignore
    }
    try {
      source?.close();
    } catch {
      // ignore
    }
  };
}
